/***
 *
 *   USERS
 *   Manage individual users registered to your application
 *
 **********/

import { useContext, useEffect, useState } from "react";
import {
  ViewContext,
  AuthContext,
  Card,
  Table,
  Animate,
  useAPI,
  Search,
  Paginate,
  Icon
} from "components/lib";
import NewModal from "@/components/modal/newModal";
import PlayerModal from "./modals/playerModal";
import ClassModal from "./modals/classModal";

export function Players() {
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);

  // state
  const [playerModal, setPlayerModal] = useState<any>(false);
  const [classesModal, setClassesModal] = useState<any>(false);
  const [search, setSearch] = useState<any>("");
  const [page, setPage] = useState<any>(1);
  const [players, setPlayers] = useState<any>([]);
  const [totalPages, setTotalPages] = useState<any>(1);
  const [selectedPlayer, setSelectedPlayer] = useState<any>({});

  // show 20 results at a time
  const limit = 20;

  const {
    data: playersData,
    loading: playersLoading,
    reload
  } = useAPI(
    `${authContext.remote_server}/api/player/search/admin?firstName=${search}&lastName=${search}&page=${page}&limit=${limit}`
  );

  function editPlayer(data: any) {
    setSelectedPlayer(data);
    setPlayerModal(true);
  }

  function deletePlayer(data: any) {
    viewContext.modal.show(
      {
        form: {},
        buttonText: "Delete",
        text: `Are you sure you want to delete ${data.email}?`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `/api/user/${data.id}`,
        method: "DELETE",
        destructive: true
      },
      () => {
        viewContext.notification.show(
          `${data.email} was deleted`,
          "success",
          true
        );
        setPlayers((prev: any) =>
          prev.filter((player: any) => player.id !== data.id)
        );
      }
    );
  }

  function pausePlayer(data: any) {
    viewContext.modal.show(
      {
        form: {
          hidden: {
            type: "hidden",
            value: true
          }
        },
        buttonText: "Pause",
        text: `Are you sure you want to pause ${data.name}? It wont be visible at the website anymore.`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `${authContext.remote_server}/api/player/${data.id}`,
        method: "PATCH",
        destructive: true
      },
      () => {
        viewContext.notification.show(`${data.name} paused`, "success", true);
        setPlayers((prev: any) =>
          [...prev].map((player: any) => {
            if (player.id === data.id) {
              player.hidden = true;
            }
            return player;
          })
        );
      }
    );
  }

  function unPausePlayer(data: any) {
    viewContext.modal.show(
      {
        form: {
          hidden: {
            type: "hidden",
            value: false
          }
        },
        buttonText: "Unpause",
        text: `Are you sure you want to unpause ${data.name}? It will be visible at the website again.`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `${authContext.remote_server}/api/player/${data.id}`,
        method: "PATCH"
      },
      () => {
        viewContext.notification.show(`${data.name} unpaused`, "success", true);
        setPlayers((prev: any) =>
          prev.map((player: any) => {
            if (player.id === data.id) {
              player.hidden = false;
            }
            return player;
          })
        );
      }
    );
  }

  useEffect(() => {
    if (playersData?.data?.data?.length > 0) {
      const playersList = playersData.data.data.map((player: any) => ({
        id: player.id,
        number: player.shirt || "0",
        name: player.first_name + " " + player.last_name,
        email: player.email || "No email",
        class: player.info?.class || "00",
        position: player.info?.position || "",
        hidden: !!player.hidden,
        ...player
      }));

      setPage(playersData.data.currentPage);
      setPlayers(playersList);
      setTotalPages(playersData.data.totalPages);
    }

    !playerModal && setSelectedPlayer({});
  }, [playersData, playerModal]);

  return (
    <Animate>
      <div className="flex gap-2 items-center justify-end mb-4">
        <button
          className="bg-[#CF2027] text-white py-3 px-5 flex justify-center items-center gap-2 shadow-md rounded-full hover:bg-[#921a1e] transition-colors"
          onClick={() => setClassesModal(true)}
        >
          <Icon image="edit" size={15} />
          Edit Classes
        </button>
        <button
          className="bg-[#CF2027] text-white py-3 px-5 flex justify-center items-center gap-2 shadow-md rounded-full hover:bg-[#921a1e] transition-colors"
          onClick={() => setPlayerModal(true)}
        >
          <Icon image="plus-circle" size={15} />
          Create Player
        </button>
      </div>
      <Card>
        <div className="flex items-center justify-between gap-4 w-full">
          <Search
            throttle={1000}
            callback={(x) => setSearch(x)}
            className="w-full mb-4"
          />

          <Paginate
            page={page}
            limit={limit}
            total={totalPages || 1}
            onChange={(x) => setPage(x)}
            className="w-fit mb-4 gap-2 justify-end border-l-2 border-gray-400 pl-4"
          />
        </div>
        <Table
          data={players}
          loading={playersLoading}
          show={["number", "name", "email", "class", "position"]}
          actions={{
            edit: editPlayer,
            delete: deletePlayer,
            custom: [
              {
                action: (data) => unPausePlayer(data),
                icon: "play",
                condition: { col: "hidden", value: true }
              },
              {
                action: (data) => pausePlayer(data),
                icon: "pause",
                condition: { col: "hidden", value: false }
              }
            ]
          }}
        />
      </Card>
      {playerModal && (
        <NewModal
          show={playerModal}
          setShow={setPlayerModal}
          Form={PlayerModal}
          reload={reload}
          formData={selectedPlayer}
        />
      )}

      {classesModal && (
        <NewModal
          show={classesModal}
          setShow={setClassesModal}
          Form={ClassModal}
          reload={reload}
        />
      )}
    </Animate>
  );
}
